import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export default function PWAInstallPrompt() {
  const [show, setShow] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(display-mode: standalone)').matches) return;
    if (localStorage.getItem('pwa_dismissed')) return;

    const isMobile = /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent) || 
      (navigator.maxTouchPoints > 1 && window.innerWidth < 1024);
    if (!isMobile) return;

    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    // Listen for beforeinstallprompt (captures new events after manifest loads)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Poll for pin_verified + prompt availability
    const interval = setInterval(() => {
      const pinOk = sessionStorage.getItem('pin_verified') === 'true';
      const notPinPage = window.location.pathname !== '/' && window.location.pathname !== '';
      if (!pinOk || !notPinPage) return;

      // Check for globally captured prompt
      if (window.__pwaPrompt && !deferredPrompt) {
        setDeferredPrompt(window.__pwaPrompt);
        setShow(true);
        clearInterval(interval);
      }

      // iOS: show install instructions
      if (ios && !show) {
        setShow(true);
        clearInterval(interval);
      }
    }, 1000);

    const timeout = setTimeout(() => clearInterval(interval), 30000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, []);

  // Also trigger show when deferredPrompt becomes available
  useEffect(() => {
    if (deferredPrompt) {
      const pinOk = sessionStorage.getItem('pin_verified') === 'true';
      const notPinPage = window.location.pathname !== '/' && window.location.pathname !== '';
      if (pinOk && notPinPage) setShow(true);
    }
  }, [deferredPrompt]);

  const handleInstall = async () => {
    const prompt = deferredPrompt || window.__pwaPrompt;
    if (prompt) {
      prompt.prompt();
      const result = await prompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
      window.__pwaPrompt = null;
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa_dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 p-4 bg-card border border-border rounded-2xl shadow-2xl max-w-md mx-auto animate-in slide-in-from-bottom-5" data-testid="pwa-install-prompt">
      <button onClick={handleDismiss} className="absolute top-2 right-2 text-muted-foreground hover:text-foreground">
        <X className="w-4 h-4" />
      </button>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-xl bg-primary/10">
          <Smartphone className="w-6 h-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Installer l'application</h3>
          {isIOS ? (
            <p className="text-xs text-muted-foreground mt-1">
              Appuyez sur <span className="inline-flex items-center"><Download className="w-3 h-3 mx-0.5" /></span> puis "Sur l'écran d'accueil"
            </p>
          ) : (
            <>
              <p className="text-xs text-muted-foreground mt-1">
                Accédez rapidement depuis votre écran d'accueil
              </p>
              {(deferredPrompt || window.__pwaPrompt) && (
                <Button size="sm" className="mt-2" onClick={handleInstall}>
                  <Download className="w-4 h-4 mr-1" /> Installer
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
